// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import {Component, OnInit} from '@angular/core';
import {UserService} from '../../../services/user-service';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {catchError, switchMap} from 'rxjs/operators';
import { IInvitationResponse, IUser, LoginStatus } from '../../../types/user';
import {EMPTY, forkJoin, from, of} from 'rxjs';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {CustomValidators} from '../../shared/validators/custom-validators';
import {ISoftwareVersion} from '../../../types/subscription';
import {D3eService} from '../../../services/d3e-service';
import {AuthService} from '../../../services/auth-service';
import {MatDialog} from '@angular/material/dialog';
import {IWelcomeDialogConfig, WelcomeComponent} from './welcome/welcome.component';
import {TranslateService} from '@ngx-translate/core';
import { IPasswordComponent, IPasswordComponentHelper } from '../../../types/password';
import { AlertDialogComponent } from '../../dialogs/alert/alert.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ConfigurationService } from '../../../services/configuration-service';
import {AppState} from '../../store/app.state';
import {Store} from '@ngrx/store';
import {loadUser} from '../../store/user/user.actions';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-d3e-invitation',
    templateUrl: './d3e-invitation.component.html',
    styleUrls: ['./d3e-invitation.component.scss'],
    standalone: false
})
export class D3eInvitationComponent implements OnInit, IPasswordComponent {

    // The following block are IPasswordComponent variables
    hasLowerCase = false;
    hasUpperCase = false;
    hasUnicode = false;
    hasNumber = false;
    hasSpecial = false;
    categoriesSatisfied = 0;
    tlMustHave1: string;
    tlMustHave2: string;
    tlMustHave3: string;
    tlMustHave4: string;
    tlUppercase: string;
    tlLowercase: string;
    tlLetter: string;
    tlUnicode: string;
    tlUnicode2: string;
    tlNumber: string;
    tlSpecialCharacter: string;
    showPass: boolean;

    fg: UntypedFormGroup = null;
    invitationResponse: IInvitationResponse = null;
    version: ISoftwareVersion = null;
    invitationExpired = false;
    invitationUUID: string = null;
    errorFindingInvitation = false;
    disableButton = false;

  constructor(
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private d3eService: D3eService,
    private authService: AuthService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private readonly store: Store<AppState>,
    private configuration: ConfigurationService
  ) {
        const keys = [];
        IPasswordComponentHelper.addTranslationKeys(keys);

        this.translate.get(keys).subscribe(vals => {
         IPasswordComponentHelper.setTranslationValues(this, vals);
        });

        this.fg = fb.group({
          firstName: [''],
          lastName: [''],
          password: [null, CustomValidators.getPasswordValidators(this)],
          confirmPassword: [null, CustomValidators.getPasswordValidators(this)],
        },
          {validator: CustomValidators.passwordMatchValidator('password', 'confirmPassword')}
        );

        this.d3eService.get_d3e_software_version()
            .pipe(untilDestroyed(this))
            .subscribe((version: ISoftwareVersion) => this.version = version);

        this.showPass = false;
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(
        switchMap((params: ParamMap) => {
          this.invitationUUID = params.get('uuid');
          return forkJoin({
              inviteResponse: this.userService.api_get_user_by_invitation(this.invitationUUID),
              invitationUUID: of(this.invitationUUID)
          });
        }),
    ).subscribe(
        result => {
            this.invitationResponse = result.inviteResponse;
            if (result.inviteResponse.expired) {
                this.invitationExpired = true;
            }
        },
        error => this.errorFindingInvitation = true
    );
  }

  showPasswordError(errorType) {
    return IPasswordComponentHelper.showPasswordError(errorType, this.p);
  }

  onSubmit() {
      this.disableButton = true;
      this.userService.resetPasswordByInvitation(
          this.invitationResponse.email,
          this.invitationUUID,
          this.fg.value.password
      ).pipe(
          switchMap(() => {
              return this.userService.userLogin(
                  this.invitationResponse.email,
                  this.fg.value.password,
                 '',
                 '',
                 '',
                 false);
          }),
          switchMap(loginResponse => {
            const translatePostfix = this.configuration.translatePostfix;
            const firstLine = this.translate.instant('accept-policies.first-line' + translatePostfix);
            const secondLine = this.translate.instant('accept-policies.second-line' + translatePostfix);
            const thirdLine = `${this.translate.instant('accept-policies.third-line' + translatePostfix)}`;
            const dialogRef = this.dialog.open(AlertDialogComponent, {
                width: '450px',
                data: {
                  unsafeHTML: `<div>${firstLine}</div>` +
                      `<div>${secondLine}</div></br>` +
                      `<div>${thirdLine}</div>`,
                  acceptBtnName: this.translate.instant('CONTINUE'),
                  rejectBtnName: this.translate.instant('CANCEL'),
                  showRejectBtn: true,
                },
              });
              return forkJoin({dialogResult: dialogRef.afterClosed(), loginResponse: of(loginResponse)});
          }),
          switchMap(({dialogResult, loginResponse}) => {
                if (dialogResult) {
                    return of(loginResponse);
                } else {
                    throw {status: LoginStatus.NEEDS_POLICY_ACCEPTANCE};
                }
          }),
          switchMap(loginResponse => {
              this.authService.login(loginResponse.token, loginResponse.mapToken, true, null);
              this.store.dispatch(loadUser());

              const user: IUser = {
                  email: this.invitationResponse.email,
                  fname: this.fg.value.firstName,
                  lname: this.fg.value.lastName,
              };
              if (user.fname || user.lname) {
                  return this.userService.api_put_user(user).pipe(
                      switchMap(() => of(user))
                  );
              }
              return of(user);
          }),
          switchMap(() => {
              const dialogConfig: IWelcomeDialogConfig = {
                  version: this.version,
                  invitationResponse: this.invitationResponse,
              }
              this.dialog.open(WelcomeComponent, {
                  width: '1000px',
                  data: dialogConfig
              });
              return from(this.router.navigate(['encryption']));
          }),
          catchError(error => {
              console.error(error);
              if (error?.status === LoginStatus.NEEDS_POLICY_ACCEPTANCE) {
                this.router.navigateByUrl('/login');
              }
              return EMPTY;
          })
      ).subscribe();
  }

  get p() {
    return this.fg.controls;
  }

}
